import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { actionChannel, call, delay, put, race, select, take } from 'typed-redux-saga';

import { SELF } from '../../../../../shared/constants';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { ConferenceStateInfo, SummarizationMailRecipient } from '../../../../../types';
import { getConferenceConfig, getConferenceStateVersion, getMyUserId, getParticipants } from '../../../selectors';
import { DisconnectReason } from '../../../types';
import { selectCloudRecordingStatus, updateCloudRecordingStatus } from '../../cloud-recording';
import { getPermissionsVersion } from '../../permissions';
import { updatePermissionsInfoSaga } from '../../permissions/update-permissions-saga';
import {
    selectIsSummarizationMailForParticipants,
    selectSummarizationStatus,
    updateSummarizationMailRecipient,
    updateSummarizationStatus,
} from '../../summarization';
import {
    isConferenceStatePayload,
    isPeersStatePayload,
    isPermissionsStatePayload,
} from '../helpers/notifications-type-guards';
import { disconnectConferenceAction, forceRequestStates, joinConference, setConferenceStateVersion } from '../slice';
import { disconnectConferenceSaga } from './disconnect';
import { joinConferenceSaga } from './join';
import { updatePeersInfoSaga } from './participants';

const logger = createLogger('goloom:conference');

/** Интервал для полного перезапроса медиа-состояний участников */
const FULL_SYNC_CONFERENCE_STATES_INTERVAL = 30000;

function* requestStates() {
    const conferenceConfig = yield* select(getConferenceConfig);
    const myUserId = yield* select(getMyUserId);

    if (!conferenceConfig || !myUserId) {
        return;
    }

    const participants = yield* select(getParticipants);

    const requestPeersData = participants.map((participant) => ({
        peer_id: participant.id === SELF ? myUserId : participant.id,
        version: participant.version ?? -1,
    }));

    const conferenceVersion = yield* select(getConferenceStateVersion);
    const requestConferenceData = {
        version: conferenceVersion,
    };

    const permissionsVersion = yield* select(getPermissionsVersion);
    const requestPermissionsData = {
        version: permissionsVersion,
    };

    const payload: CloudAPI.GetPeersState.Request = {
        peers: requestPeersData,
        conference: requestConferenceData,
        permissions: requestPermissionsData,
    };

    try {
        const states = yield* call([cloudApi, cloudApi.getPeersState], conferenceConfig.resolved_uri, payload);

        return states;
    } catch (error) {
        logger.error(error, { method: 'requestStates' });
    }
}

export function* startPollConference() {
    const conferenceConfig = yield* select(getConferenceConfig);

    const participantPollInterval = conferenceConfig?.client_configuration?.state_check_interval_seconds
        ? conferenceConfig?.client_configuration?.state_check_interval_seconds * 1000
        : FULL_SYNC_CONFERENCE_STATES_INTERVAL;

    while (true) {
        yield* race([delay(participantPollInterval), take(forceRequestStates)]);

        const states = yield* call(requestStates);
        if (!states) {
            continue;
        }
        if (isPeersStatePayload(states)) {
            yield* call(updatePeersInfoSaga, states.peers);
        }

        if (isPermissionsStatePayload(states)) {
            yield* call(updatePermissionsInfoSaga, states.permissions);
        }

        if (isConferenceStatePayload(states)) {
            yield* call(syncConferenceState, states.conference);
        }
    }
}

export function* syncConferenceState(conferenceStateInfo: ConferenceStateInfo) {
    const { version, state } = conferenceStateInfo;
    const { cloud_recording_status, summarization_status, summarization_mail_recipient } = state;

    yield* put(setConferenceStateVersion(version));

    if (cloud_recording_status) {
        const currentStatus = yield* select(selectCloudRecordingStatus);

        if (currentStatus !== cloud_recording_status) {
            yield* call(updateCloudRecordingStatus, cloud_recording_status);
        }
    }

    if (summarization_mail_recipient) {
        const isSummarizationMailForParticipants =
            summarization_mail_recipient === SummarizationMailRecipient.PARTICIPANTS_FROM_ORGANIZATION;
        const currentIsSummarizationMailForParticipants = yield* select(selectIsSummarizationMailForParticipants);

        if (currentIsSummarizationMailForParticipants !== isSummarizationMailForParticipants) {
            yield* call(updateSummarizationMailRecipient, isSummarizationMailForParticipants);
        }
    }

    if (summarization_status) {
        const currentStatus = yield* select(selectSummarizationStatus);

        if (currentStatus !== summarization_status) {
            yield* call(updateSummarizationStatus, summarization_status);
        }
    }
}

export function* watchConference() {
    const joinConferenceActionChannel = yield* actionChannel(joinConference);

    while (true) {
        try {
            const action = yield* take(joinConferenceActionChannel);

            const result = yield* race({
                join: call(joinConferenceSaga, action),
                cancel: take(disconnectConferenceAction),
            });

            yield* call(disconnectConferenceSaga, {
                error: result.cancel?.payload.error,
                source: result.cancel?.payload.source ?? DisconnectReason.UNKNOWN,
            });
        } catch (error) {
            logger.error(error, {
                method: 'watchConference',
            });
        }
    }
}
